/**
 * Smart Location Picker Component for HoneyDo Platform
 * Provides intelligent location selection with search, suggestions, and weather integration
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Modal, Button, Alert, Spinner, Badge } from 'flowbite-react';
import { TextField } from '../ui/FlowbiteForm';
import { 
  locationIntelligenceService,
  LocationDetails,
  LocationSuggestion,
  TaskLocationInference,
  WeatherLocationData
} from '../../services/LocationIntelligenceService';

interface LocationPickerProps {
  isOpen: boolean;
  onClose: () => void;
  onLocationSelect: (location: LocationDetails | null) => void;
  taskTitle?: string;
  taskDescription?: string;
  taskCategory?: string;
  currentLocation?: string;
  showWeatherInfo?: boolean;
  allowCustomLocation?: boolean;
  placeholder?: string;
}

interface LocationOption extends LocationDetails {
  weatherData?: WeatherLocationData;
  travelTime?: number;
  suggestion?: {
    reason: string;
    confidence: number;
  };
}

export const LocationPicker: React.FC<LocationPickerProps> = ({
  isOpen,
  onClose,
  onLocationSelect,
  taskTitle = '',
  taskDescription = '',
  taskCategory,
  currentLocation,
  showWeatherInfo = true,
  allowCustomLocation = true,
  placeholder = "Search for a location or address..."
}) => {
  const [searchQuery, setSearchQuery] = useState(currentLocation || '');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<LocationOption[]>([]);
  const [suggestions, setSuggestions] = useState<LocationSuggestion[]>([]);
  const [inference, setInference] = useState<TaskLocationInference | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<LocationDetails | null>(null);
  const [error, setError] = useState<string>('');
  const [showCurrentLocationPrompt, setShowCurrentLocationPrompt] = useState(false);
  
  // Quick access locations
  const [homeLocation, setHomeLocation] = useState<LocationDetails | null>(null);
  const [workLocation, setWorkLocation] = useState<LocationDetails | null>(null);
  const [favoriteLocations, setFavoriteLocations] = useState<LocationDetails[]>([]);
  const [recentLocations, setRecentLocations] = useState<LocationDetails[]>([]);

  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Load initial data when component mounts
  useEffect(() => {
    if (isOpen) {
      loadInitialData();
    }
  }, [isOpen, taskTitle, taskDescription, taskCategory]);

  // Debounced search
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (searchQuery.trim().length > 2) {
      searchTimeoutRef.current = setTimeout(() => {
        performSearch(searchQuery);
      }, 300);
    } else {
      setSearchResults([]);
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  const loadInitialData = async () => {
    try {
      // Load saved locations
      setHomeLocation(locationIntelligenceService.getHomeLocation() || null);
      setWorkLocation(locationIntelligenceService.getWorkLocation() || null);
      setFavoriteLocations(locationIntelligenceService.getFavoriteLocations());
      setRecentLocations(locationIntelligenceService.getRecentLocations());

      // Get task location inference
      if (taskTitle) {
        const taskInference = await locationIntelligenceService.inferTaskLocation(
          taskTitle,
          taskDescription,
          taskCategory
        );
        setInference(taskInference);

        // Get location suggestions
        const locationSuggestions = await locationIntelligenceService.getLocationSuggestions(
          taskTitle,
          taskDescription,
          taskCategory
        );
        setSuggestions(locationSuggestions);

        // Pre-populate search if there's a strong suggestion
        if (taskInference.suggestedLocation && taskInference.confidence > 0.8) {
          setSelectedLocation(taskInference.suggestedLocation);
          if (!currentLocation) {
            setSearchQuery(taskInference.suggestedLocation.name);
          }
        }
      }

      // Check if we should prompt for current location
      const currentPos = locationIntelligenceService.getCurrentLocation();
      if (!currentPos && !homeLocation) {
        setShowCurrentLocationPrompt(true);
      }
    } catch (error) {
      console.error('Failed to load location data:', error);
      setError('Failed to load location data');
    }
  };

  const performSearch = async (query: string) => {
    if (!query.trim()) return;

    setIsSearching(true);
    setError('');

    try {
      const geocoded = await locationIntelligenceService.geocodeLocation(query);
      const results: LocationOption[] = [];

      if (geocoded) {
        results.push(geocoded);

        // Get weather data if requested
        if (showWeatherInfo) {
          const weatherData = await locationIntelligenceService.getMultiLocationWeather([geocoded]);
          if (weatherData.length > 0) {
            results[0].weatherData = weatherData[0];
          }
        }

        // Calculate travel time from home if available
        if (homeLocation && geocoded.id !== homeLocation.id) {
          const travelTime = await locationIntelligenceService.calculateTravelTime(
            homeLocation,
            geocoded,
            'driving'
          );
          if (travelTime) {
            results[0].travelTime = travelTime.durationMinutes;
          }
        }
      }

      // Add nearby places if query suggests a business type
      if (query.toLowerCase().includes('store') || query.toLowerCase().includes('depot')) {
        const nearbyPlaces = await locationIntelligenceService.findNearbyPlaces('home_improvement', 3);
        const enhancedPlaces = nearbyPlaces.map(place => ({ ...place } as LocationOption));
        results.push(...enhancedPlaces);
      }

      setSearchResults(results);
    } catch (error) {
      console.error('Search failed:', error);
      setError('Search failed. Please try again.');
    } finally {
      setIsSearching(false);
    }
  };

  const handleLocationSelect = async (location: LocationDetails) => {
    setSelectedLocation(location);
    setSearchQuery(location.name);
    
    // Add to recent locations
    await locationIntelligenceService.addToRecent(location);
  };

  const handleConfirmSelection = () => {
    onLocationSelect(selectedLocation);
    onClose();
  };

  const handleNoLocation = () => {
    onLocationSelect(null);
    onClose();
  };

  const handleUseCurrentLocation = async () => {
    try {
      const currentPos = locationIntelligenceService.getCurrentLocation();
      if (currentPos) {
        const geocoded = await locationIntelligenceService.reverseGeocode(currentPos);
        if (geocoded) {
          const location: LocationDetails = {
            id: 'current_location',
            name: 'Current Location',
            address: geocoded.formatted_address || geocoded.display_name,
            coordinates: currentPos,
            type: 'address',
            isVerified: true,
            confidence: 0.9
          };
          handleLocationSelect(location);
        }
      }
    } catch (error) {
      setError('Failed to get current location');
    }
    setShowCurrentLocationPrompt(false);
  };

  const renderLocationOption = (location: LocationOption, isSelected = false) => {
    return (
      <div
        key={location.id}
        className={`p-3 rounded-lg border cursor-pointer transition-all ${
          isSelected 
            ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20' 
            : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
        }`}
        onClick={() => handleLocationSelect(location)}
      >
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-900 dark:text-white">
                {location.name}
              </span>
              {location.type === 'home' && <span className="text-sm">🏠</span>}
              {location.type === 'work' && <span className="text-sm">🏢</span>}
              {location.category === 'home_improvement' && <span className="text-sm">🔨</span>}
              {location.category === 'outdoor' && <span className="text-sm">🌳</span>}
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {location.address}
            </p>
            
            {/* Travel time and weather info */}
            <div className="flex items-center gap-4 mt-2">
              {location.travelTime && (
                <div className="flex items-center gap-1 text-xs text-gray-500">
                  <span>🚗</span>
                  <span>{location.travelTime} min</span>
                </div>
              )}
              {location.weatherData && (
                <div className="flex items-center gap-1 text-xs text-gray-500">
                  <span>🌤️</span>
                  <span>{location.weatherData.currentWeather.conditions}</span>
                  <span>{location.weatherData.currentWeather.tempHigh}°F</span>
                  {location.weatherData.currentWeather.rainChance > 30 && (
                    <span className="text-blue-600">
                      {location.weatherData.currentWeather.rainChance}% rain
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Suggestion reason */}
            {location.suggestion && (
              <div className="mt-2">
                <Badge color="purple" size="sm">
                  {location.suggestion.reason}
                </Badge>
              </div>
            )}
          </div>
          
          {/* Weather suitability indicator */}
          {location.weatherData && showWeatherInfo && (
            <div className="ml-2">
              {location.weatherData.suitabilityScore > 0.8 ? (
                <span className="text-green-500 text-sm">✅ Good weather</span>
              ) : location.weatherData.suitabilityScore > 0.5 ? (
                <span className="text-yellow-500 text-sm">⚠️ Fair weather</span>
              ) : (
                <span className="text-red-500 text-sm">❌ Poor weather</span>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderQuickLocationSection = (title: string, locations: LocationDetails[], icon: string) => {
    if (locations.length === 0) return null;

    return (
      <div className="mb-4">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
          <span>{icon}</span>
          {title}
        </h4>
        <div className="space-y-2">
          {locations.slice(0, 3).map(location => renderLocationOption(location as LocationOption))}
        </div>
      </div>
    );
  };

  return (
    <Modal show={isOpen} onClose={onClose} size="2xl">
      <div className="p-6">
        <div className="flex items-center justify-between pb-4 mb-4 border-b border-gray-200">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
            📍 Choose Location
          </h3>
          <Button color="gray" size="sm" onClick={onClose}>
            ✕
          </Button>
        </div>

        {/* Task inference info */}
        {inference && (
          <div className={`p-3 rounded-lg mb-4 ${
            inference.locationRequired 
              ? 'bg-yellow-50 border border-yellow-200 dark:bg-yellow-900/20' 
              : 'bg-blue-50 border border-blue-200 dark:bg-blue-900/20'
          }`}>
            <p className="text-sm">
              <span className="font-medium">Smart Detection:</span> {inference.inferenceReason}
              {inference.needsUserConfirmation && (
                <span className="text-yellow-600 dark:text-yellow-400 ml-2">
                  (Please confirm)
                </span>
              )}
            </p>
          </div>
        )}

        {/* Current location prompt */}
        {showCurrentLocationPrompt && (
          <Alert color="info" className="mb-4">
            <div className="flex items-center justify-between">
              <span>Use your current location for this task?</span>
              <div className="flex gap-2">
                <Button size="xs" color="purple" onClick={handleUseCurrentLocation}>
                  Use Current
                </Button>
                <Button size="xs" color="gray" onClick={() => setShowCurrentLocationPrompt(false)}>
                  Skip
                </Button>
              </div>
            </div>
          </Alert>
        )}

        {/* Search input */}
        <div className="mb-4">
          <TextField
            id="location-search"
            label="Search Location"
            placeholder={placeholder}
            value={searchQuery}
            onChange={setSearchQuery}
          />
        </div>

        {/* Error message */}
        {error && (
          <Alert color="failure" className="mb-4">
            {error}
          </Alert>
        )}

        <div className="max-h-96 overflow-y-auto">
          {/* Smart suggestions */}
          {suggestions.length > 0 && (
            <div className="mb-6">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                <span>🎯</span>
                Smart Suggestions
              </h4>
              <div className="space-y-2">
                {suggestions.slice(0, 3).map(suggestion => {
                  const location = { 
                    ...suggestion.location, 
                    suggestion: { 
                      reason: suggestion.reason, 
                      confidence: suggestion.confidence 
                    }
                  } as LocationOption;
                  return renderLocationOption(location);
                })}
              </div>
            </div>
          )}

          {/* Search results */}
          {searchResults.length > 0 && (
            <div className="mb-6">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Search Results
              </h4>
              <div className="space-y-2">
                {searchResults.map(result => renderLocationOption(result))}
              </div>
            </div>
          )}

          {/* Quick access locations */}
          {homeLocation && renderQuickLocationSection('Home', [homeLocation], '🏠')}
          {workLocation && renderQuickLocationSection('Work', [workLocation], '🏢')}
          {renderQuickLocationSection('Favorites', favoriteLocations, '⭐')}
          {renderQuickLocationSection('Recent', recentLocations, '🕒')}
        </div>

        {/* Action buttons */}
        <div className="flex justify-between items-center pt-4 border-t border-gray-200">
          <div>
            {allowCustomLocation && !inference?.locationRequired && (
              <Button color="gray" onClick={handleNoLocation}>
                No Specific Location
              </Button>
            )}
          </div>
          <div className="flex gap-3">
            <Button color="gray" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              color="purple" 
              onClick={handleConfirmSelection}
              disabled={!selectedLocation && inference?.locationRequired}
            >
              {selectedLocation ? `Select ${selectedLocation.name}` : 'Select Location'}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default LocationPicker;